/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ROUTES } from '../../constants/routes';
import { useHistory, useParams } from 'react-router';
import { generateUrl } from '../../routes/util';
import Dashboard, { DashboardRow, DashboardSection } from '../dashboard';
import { AssuranceSummaryTitle } from '../assurance/assurance-summary-title';
import { AssuranceSummaryContainer } from '../assurance/assurance-summary-container';
import SurveyQuestionList, { BulkActionProps, QuestionListColumn } from '../survey-question-list/survey-question-list';
import Loader from '../loader';
import { ReduxReducer, useAppDispatch, useAppSelector } from '../../reducers';
import { BasicAlert } from '../alert/BasicAlert';
import G17Client from '../../services/G17Client';
import { AssurancePortfolioStatus, AssuranceStatus, OrganizationAssurancePortfolio } from '../../types/assurance';
import { formatDate } from '../../utils/date';
import SimpleTooltip from '../simple-tooltip';
import { CompleteAssuranceModal } from './complete-assurance-modal';
import { downloadPortfolioBundle } from '../../actions/assurance';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { AssuranceQuestionView } from './assurance-question-view';
import { ManageStakeholderGroup } from './stakeholders/manage-stakeholder-group';
import TeamMemberContainer from './stakeholders/TeamMemberContainer';
import { getCurrentUser } from '../../selectors/user';
import NotAuthorised from '../../routes/not-authorised';
import { ActionsToolbar } from '../survey-question-list/partials/ActionsToolbar';
import { DownloadButton } from './toolbar-buttons/DownloadButton';
import { AssureButton } from './toolbar-buttons/AssureButton';
import { DelegateButton } from './toolbar-buttons/DelegateButton';
import { ColumnTitle } from '../survey-question-list/partials/ColumnTitle';
import { Divider } from '../tracking-list';
import { ColumnValue } from '../survey-question-list/partials/ColumnValue';
import { ColumnAssuranceUtrvStatus } from './partials/ColumnAssuranceUtrvStatus';
import { SurveyQuestionListToolbar } from '../survey/survey-question-list-toolbar';
import { ScopeView, ScopeViewDropdown } from '../survey/survey-question-list-toolbar/partials/ScopeViewDropdown';
import { Spacer } from '../survey/survey-question-list-toolbar/partials/Spacer';
import { useSurveyGroups } from '../../utils/survey';
import { ExpandAllToggle } from '../survey/survey-question-list-toolbar/partials/ExpandAllToggle';
import { BlockingLoader } from '../loader/BlockingLoader';
import './assurer-assurance-portfolio.scss';
import { ColumnCode } from '../survey-question-list/partials/ColumnCode';
import { SurveyOverviewMode } from '../../slice/surveySettings';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { QUESTION } from '../../constants/terminology';
import { ColumnBookmark } from '../survey-question-list/partials/ColumnBookmark';

interface RouteParams {
  assurancePortfolioId: string,
  questionId?: string,
  index?: string
}

export interface AssuranceSettingsType {
  text: string;
  tooltip: string;
  handler: () => void;
  icon?: string;
  isCompleted?: boolean;
  isDisabled?: boolean;
}

export const portfolioBundleDownload = (_id: string, portfolioInitiativeId: string) => {
  return downloadPortfolioBundle(_id)
    .then(async ([document]) => {
      if (!document || !document.url) {
        return;
      }

      window.open(document.url, '_blank', '');

      const analytics = getAnalytics();
      return analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
        assurancePortfolioId: _id,
        initiativeId: portfolioInitiativeId,
        source: 'survey_assurance'
      });
    })
}

export const assurancePortfolioRoute = {
  id: 'assurance_portfolio',
  label: 'Assurance Tracker',
  tooltip: 'Assurance Tracker',
  path: '/assurance/portfolio/:assurancePortfolioId/:questionId?/:index?',
  icon: 'fa-folder-open',
  appPermissionId: 'app_assurance',
  exact: false,
  auth: true,
}

const AssurerAssurancePortfolio = () => {

  const dispatch = useAppDispatch();
  const history = useHistory();
  const currentUser = useAppSelector(getCurrentUser);
  const { assurancePortfolioId, questionId, index } = useParams<RouteParams>();

  const [assuranceModal, setAssuranceModal] = React.useState({ open: false, allowConfirm: false });
  const [assuranceState, setPortfolioState] = useState<ReduxReducer<OrganizationAssurancePortfolio | undefined>>({
    loaded: false,
    errored: false,
    data: undefined
  })
  const [isStakeholderOpen, setStakeholderOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [assuranceOverviewMode, setAssuranceOverviewMode] = useState<SurveyOverviewMode>(SurveyOverviewMode.QuestionList);

  const handleSelectOverviewMode = (view: ScopeView) => setAssuranceOverviewMode(view.value);

  const toggle = () => setStakeholderOpen(isOpen => !isOpen);
  const closePopup = () => {
    setStakeholderOpen(isOpen => !isOpen)
    handleReload();
  }

  const assurancePortfolio = assuranceState.data;
  const isCompleted = assurancePortfolio?.status === AssurancePortfolioStatus.Completed;

  const lookupMap = useMemo(
    () =>
      new Map(
        assurancePortfolio?.universalTrackerValueAssurances.map((assurance) => [assurance.utrvId, assurance._id])
      ),
    [assurancePortfolio?.universalTrackerValueAssurances]
  );

  const currentAssurer = assurancePortfolio?.assurers.find(assurer => assurer._id === currentUser?._id);

  const handleReload = useCallback((isBlocking: boolean = true) => {
    if (assurancePortfolioId) {
      setPortfolioState((s) => ({ ...s, loaded: isBlocking ? false : s.loaded }))
      G17Client.getAssurerPortfolio(assurancePortfolioId).then((data) => {
        setPortfolioState((s) => ({ ...s, data, loaded: isBlocking ? true : s.loaded }))
      }).catch((e: Error) => {
        setPortfolioState((s) => {
          return ({ ...s, errored: true, errorMessage: e.message });
        })
      });
    }
  }, [assurancePortfolioId])

  useEffect(() => {
    if (!assurancePortfolioId) {
      return;
    }
    handleReload();
  }, [assurancePortfolioId, dispatch, handleReload]);

  const surveyGroups = useSurveyGroups(assuranceOverviewMode, assuranceState.data?.surveyData);

  const isReadyToComplete = useMemo(() => {
    return !isCompleted && assurancePortfolio?.universalTrackerValueAssurances.some(utrvAssurance => utrvAssurance?.status === AssuranceStatus.Completed);
  }, [isCompleted, assurancePortfolio?.universalTrackerValueAssurances]);

  if (!assurancePortfolioId) {
    return <BasicAlert type={'warning'}>Portfolio not found</BasicAlert>
  }

  if (assuranceState.errored && assuranceState.errorMessage) {
    return <BasicAlert type={'danger'}>{assuranceState.errorMessage}</BasicAlert>
  }

  if (!assuranceState.loaded || !assuranceState.data || !assurancePortfolio || !currentUser) {
    return <Loader />;
  }

  if (!assurancePortfolio?.assurers || !currentAssurer) {
    return <NotAuthorised />;
  }

  const goToQuestion = (id: string, index?: number) => {
    const url = generateUrl(assurancePortfolioRoute, {
      assurancePortfolioId,
      questionId: id,
      index: String(index ?? ''),
    });
    history.push(url);
  };

  if (questionId) {
    return <AssuranceQuestionView
      assurancePortfolio={assurancePortfolio}
      surveyGroups={surveyGroups}
      questionId={questionId}
      index={index}
      handleReload={handleReload}
    />
  }

  const renderPortfolioComplete = (assurancePortfolio: OrganizationAssurancePortfolio) => {
    if (!isCompleted) {
      return '';
    }

    const hist = assurancePortfolio.history;
    const assuredDate = formatDate(hist[hist.length - 1].created);
    return <>
      <i className='fa fa-history ml-5 mr-2' />Portfolio Completed on {assuredDate}
    </>
  }

  const setCompleteAssuranceModal = (reload = false, allowConfirm = false) => {
    if (reload) {
      handleReload();
      setAssuranceModal({
        open: false,
        allowConfirm: allowConfirm
      });
    } else {
      setAssuranceModal({
        open: !assuranceModal.open,
        allowConfirm: allowConfirm
      });
    }
  };


  const settings: AssuranceSettingsType[] = [
    {
      text: 'Team members',
      tooltip: 'Add Assurance Team Members',
      icon: 'fa fa-user-plus',
      handler: toggle,
      isCompleted,
    },
    {
      text: 'Download all data & evidence',
      tooltip: 'Download all data and evidence',
      icon: 'fa fa-file-archive',
      handler: () => {
        setLoading(true);
        portfolioBundleDownload(assurancePortfolio._id, assurancePortfolio.initiativeId)
        .catch((e: Error) => {
          dispatch(addSiteAlert({
            content: 'There was a problem downloading',
            color: SiteAlertColors.Danger
          }))
        })
        .finally(() => setLoading(false))
      },
    },
    {
      text: 'Complete Assurance',
      tooltip: 'Complete Assurance Tracker and Attach Statement',
      handler: () => setCompleteAssuranceModal(false, true),
      isCompleted,
      isDisabled: !isReadyToComplete,
    },
  ];

  const renderSettings = () => {
    return settings.map((setting) =>
      setting.isCompleted ? (
        <></>
      ) : (
        <SimpleTooltip
          text={setting.tooltip}
          component={
            <Button
              className='ml-2 px-3 mb-2'
              onClick={setting.handler}
              outline={setting.hasOwnProperty('icon')}
              disabled={setting.isDisabled}
            >
              <i className={`${setting.icon} mr-2`} />
              {setting.text}
            </Button>
          }
        />
      )
    );
  };

  const renderActionToolbar = (props: BulkActionProps) => {
    const { selectedQuestions } = props;
    const listButtons = [
      { name: 'download', Component: DownloadButton, buttonProps: { assurancePortfolio, selectedQuestions } },
      {
        name: 'follow',
        Component: DelegateButton,
        buttonProps: { assurancePortfolio, currentAssurer, selectedQuestions, handleReload },
      },
      {
        name: 'assure',
        Component: AssureButton,
        buttonProps: { assurancePortfolio, lookupMap, selectedQuestions, handleReload },
      },
    ];
    return <ActionsToolbar {...props} buttons={listButtons} />;
  };

  const columns: QuestionListColumn[] = [
    ColumnBookmark,
    ColumnCode,
    ColumnTitle,
    Divider,
    ColumnValue,
    Divider,
    (props) => (
      <ColumnAssuranceUtrvStatus
        {...props}
        AssuranceUtrvs={assurancePortfolio.universalTrackerValueAssurances}
      />
    ),
  ];
  const surveyGroupCount = surveyGroups.length;

  return (
    <div className='flex-column'>
      <div>
        <Dashboard>
          <div>
            <Button color='link'
              onClick={() => history.push(generateUrl(ROUTES.ASSURANCE))}>
              <i className='fa fa-arrow-circle-left mr-2' />
              Back to Dashboard
            </Button>
          </div>
        </Dashboard>
      </div>
      <Dashboard className='assuranceContainer'>
        <AssuranceSummaryTitle assurancePortfolio={assurancePortfolio} />
        <AssuranceSummaryContainer assurancePortfolio={assurancePortfolio} />

        <DashboardRow className='mt-3' title={`${QUESTION.CAPITALIZED_PLURAL} for assurance`} headingStyle={3}
          buttons={renderSettings()} />

        <DashboardSection padding={0}>
          <SurveyQuestionList
            surveyId={assurancePortfolio.surveyId}
            initiativeId={assurancePortfolio.initiativeId}
            surveyGroups={surveyGroups}
            goToQuestion={goToQuestion}
            noRowsMessage={`No ${QUESTION.PLURAL} have been selected for assurance`}
            bulkActionToolbar={renderActionToolbar}
            columns={columns}
            toolbar={(props) => (
              <SurveyQuestionListToolbar
                {...props}
                defaultOverviewMode={assuranceOverviewMode}
                handleSelectOverviewMode={handleSelectOverviewMode}
                surveyScope={assuranceState.data?.surveyData?.scope}
                components={[
                  ScopeViewDropdown,
                  Spacer,
                  surveyGroupCount > 1 ? ExpandAllToggle : null,
                ]}
              />
            )}
          />
        </DashboardSection>
        <DashboardRow>
          <div className='text-right flex-fill d-flex align-items-baseline flex-wrap'>
            <div className='mr-auto mb-2'>
              {renderPortfolioComplete(assurancePortfolio)}
            </div>
          </div>
          <CompleteAssuranceModal
            assurancePortfolio={assurancePortfolio}
            allowConfirm={assuranceModal.allowConfirm}
            isOpen={assuranceModal.open}
            toggle={(completed?: boolean) => setCompleteAssuranceModal(completed)}
          />
        </DashboardRow>
      </Dashboard>
      <Modal isOpen={isStakeholderOpen} toggle={closePopup} backdrop='static' className='modal-lg'>
        <ModalHeader toggle={closePopup}>Team members</ModalHeader>
        <ModalBody>
          <TeamMemberContainer>
            <ManageStakeholderGroup assurancePortfolio={assurancePortfolio} />
          </TeamMemberContainer>
        </ModalBody>
      </Modal>
      {isLoading ? <BlockingLoader /> : null}
    </div>
  );
}
export default AssurerAssurancePortfolio;
